import {
  autoUpdate,
  flip,
  offset,
  shift,
  Strategy,
  useClientPoint,
  useFloating,
  useInteractions,
} from '@floating-ui/react';
import React from 'react';

type Props = {
  /**
   * Whether to follow mouse pointer.
   */
  followMousePointer: boolean;

  /**
   * Whether to shift or flip element when element overflows viewport edge.
   */
  middleware: 'shift' | 'flip';

  /**
   * Offset to startPos or mouse pointer if followMousePointer is set to true. Also offset from
   * viewport edge before middleware executes.
   */
  offset?: { viewportEdge?: number; mousePointer?: number };

  /**
   * Start position on first render. Mouse position is not fetched on first render, only on mouse
   * move. This will prevent render at (0,0) on first render.
   */
  startPos?: { x: number; y: number };

  /**
   * Should match CSS "position" on element that ref property is attached to.
   */
  strategy: Strategy;
};

export const useFloatingPosition = (props: Props) => {
  const useStartPos = React.useRef(props.startPos !== undefined);

  const { refs, floatingStyles, context } = useFloating({
    middleware: [
      offset({
        mainAxis: props.offset?.mousePointer,
        crossAxis: props.offset?.mousePointer,
        alignmentAxis: props.offset?.mousePointer,
      }),
      props.middleware === 'shift'
        ? shift({
            padding: props.offset?.viewportEdge,
            crossAxis: true,
          })
        : flip({
            padding: props.offset?.viewportEdge,
          }),
    ],
    placement: 'bottom-start',
    strategy: props.strategy,
    whileElementsMounted: autoUpdate,
  });
  const clientPoint = useClientPoint(
    context,
    useStartPos.current
      ? {
          x: props.startPos?.x,
          y: props.startPos?.y,
        }
      : undefined,
  );
  const { getFloatingProps } = useInteractions([clientPoint]);

  // If we want to follow the mouse pointer, we need to set `x` and `y` in useClientPoint to
  // undefined. We control this with useStartPos, as this prop is not necessary when following the
  // mouse pointer (useStartPos is only used on first render).
  if (useStartPos && props.followMousePointer) {
    useStartPos.current = false;
  }

  return {
    ref: refs.setFloating,
    floatingProps: getFloatingProps(),
    floatingStyles,
  };
};
